<template>
  <div>
    <h1 class="page-title">Dashboard</h1>

    <SkeletonLoader v-if="store.loading" variant="stat-card" :count="4" />

    <div class="stats-grid" v-else>
      <div class="stat-card card">
        <div class="stat-icon stat-total">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M16 17v2H2v-2s0-4 7-4s7 4 7 4m-3.5-9.5A3.5 3.5 0 1 0 9 11a3.5 3.5 0 0 0 3.5-3.5m3.44 5.5A5.32 5.32 0 0 1 18 17v2h4v-2s0-3.63-6.06-4M15 4a3.39 3.39 0 0 0-1.93.59a5 5 0 0 1 0 5.82A3.39 3.39 0 0 0 15 11a3.5 3.5 0 0 0 0-7Z"/></svg>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ store.totalCount }}</span>
          <span class="stat-label">Total Inscritos</span>
        </div>
      </div>

      <div class="stat-card card">
        <div class="stat-icon stat-confirmed">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4l4.25 4.25ZM12 22q-2.075 0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22Z"/></svg>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ store.confirmedCount }}</span>
          <span class="stat-label">Confirmados</span>
        </div>
      </div>

      <div class="stat-card card">
        <div class="stat-icon stat-pending">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10s10-4.5 10-10S17.5 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8m.5-13H11v6l5.2 3.2l.8-1.3l-4.5-2.7V7Z"/></svg>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ store.pendingCount }}</span>
          <span class="stat-label">Pendentes</span>
        </div>
      </div>

      <div class="stat-card card">
        <div class="stat-icon stat-percent">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-7 2h1.5v3l2-3h1.7l-2 3l2 3h-1.7l-2-3v3H12V5zM7 7.25h2.5V6.5H7V5h4v3.75H8.5v.75H11V11H7V7.25zM19 19H5L19 5v14z"/></svg>
        </div>
        <div class="stat-info">
          <span class="stat-number">{{ store.checkInPercentage }}%</span>
          <span class="stat-label">Check-in</span>
        </div>
      </div>
    </div>

    <!-- Graficos -->
    <div class="charts-grid" v-if="!store.loading">
      <!-- Confirmados por Quarto -->
      <div class="chart-card card">
        <h2 class="section-title">Confirmados por Quarto</h2>
        <div class="chart-bars">
          <div class="bar-row" v-for="item in quartoStats" :key="item.quarto">
            <span class="bar-label">
              <span class="quarto-dot" :style="{ background: quartoColor(item.quarto) }"></span>
              {{ item.quarto }}
            </span>
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: barWidth(item.confirmed, maxQuartoTotal), background: quartoColor(item.quarto) }">
                <span class="bar-value" v-if="item.confirmed > 0">{{ item.confirmed }}</span>
              </div>
              <div class="bar-fill bar-fill-pending" :style="{ width: barWidth(item.pending, maxQuartoTotal) }">
                <span class="bar-value" v-if="item.pending > 0">{{ item.pending }}</span>
              </div>
            </div>
            <span class="bar-total">{{ item.confirmed }}/{{ item.total }}</span>
          </div>
        </div>
        <div class="chart-legend">
          <span class="legend-item"><span class="legend-dot legend-confirmed"></span> Confirmados</span>
          <span class="legend-item"><span class="legend-dot legend-pending"></span> Pendentes</span>
        </div>
      </div>

      <!-- Ranking de Check-ins por Usuario -->
      <div class="chart-card card">
        <h2 class="section-title">Ranking de Check-ins</h2>
        <div class="ranking-list" v-if="checkinRanking.length > 0">
          <div class="ranking-item" v-for="(item, index) in checkinRanking" :key="item.name">
            <span class="ranking-position" :class="'ranking-' + (index + 1)">{{ index + 1 }}</span>
            <span class="ranking-name">{{ item.name }}</span>
            <div class="ranking-bar-track">
              <div class="ranking-bar-fill" :style="{ width: barWidth(item.count, maxRankingCount) }"></div>
            </div>
            <span class="ranking-count">{{ item.count }}</span>
          </div>
        </div>
        <p class="chart-empty" v-else>Nenhum check-in registrado ainda.</p>
      </div>
    </div>

    <!-- Ultimos confirmados -->
    <div class="recent-section" v-if="recentCheckins.length > 0">
      <h2 class="section-title">Ultimos Check-ins</h2>
      <div class="recent-list card">
        <div class="recent-item" v-for="item in recentCheckins" :key="item.id">
          <span class="recent-name">{{ item.name }}</span>
          <div class="recent-meta">
            <span class="recent-by" v-if="item.checkedBy">{{ item.checkedBy.name }}</span>
            <span class="recent-time" v-if="item.checkedAt">{{ formatTime(item.checkedAt) }}</span>
          </div>
          <StatusBadge status="confirmed" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useParticipantsStore } from '../stores/participants'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import StatusBadge from '../components/ui/StatusBadge.vue'

export default {
  name: 'DashboardPage',
  components: { SkeletonLoader, StatusBadge },
  computed: {
    store() { return useParticipantsStore() },
    recentCheckins() {
      return [...this.store.confirmed]
        .sort((a, b) => {
          const da = a.checkedAt ? new Date(a.checkedAt) : new Date(0)
          const db = b.checkedAt ? new Date(b.checkedAt) : new Date(0)
          return db - da
        })
        .slice(0, 8)
    },
    quartoStats() {
      const map = {}
      this.store.participants.forEach(p => {
        const q = p.quarto || 'Sem quarto'
        if (!map[q]) map[q] = { quarto: q, confirmed: 0, pending: 0, total: 0 }
        map[q].total++
        if (p.check) map[q].confirmed++
        else map[q].pending++
      })
      return Object.values(map).sort((a, b) => b.total - a.total)
    },
    maxQuartoTotal() {
      return Math.max(1, ...this.quartoStats.map(q => q.total))
    },
    checkinRanking() {
      const map = {}
      this.store.confirmed.forEach(p => {
        const name = p.checkedBy?.name || 'Desconhecido'
        if (!map[name]) map[name] = { name, count: 0 }
        map[name].count++
      })
      return Object.values(map).sort((a, b) => b.count - a.count)
    },
    maxRankingCount() {
      return Math.max(1, ...this.checkinRanking.map(r => r.count))
    }
  },
  methods: {
    barWidth(value, max) {
      return Math.round((value / max) * 100) + '%'
    },
    formatTime(dateStr) {
      return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },
    quartoColor(quarto) {
      const colors = {
        'AZUL': '#3366cc',
        'VERDE': '#2ecc71',
        'AMARELO': '#f1c40f',
        'LARANJA': '#e67e22',
        'CINZA': '#95a5a6',
        'VERMELHO': '#e74c3c',
        'Sem quarto': '#bdc3c7'
      }
      return colors[quarto] || '#9b59b6'
    }
  }
}
</script>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-base);
  margin-bottom: var(--spacing-xl);
}
.stat-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-base);
  padding: var(--spacing-base) var(--spacing-lg);
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-total { background: rgba(51,102,204,0.1); color: var(--color-accent); }
.stat-confirmed { background: rgba(46,204,113,0.1); color: var(--color-success); }
.stat-pending { background: rgba(241,196,15,0.15); color: #f39c12; }
.stat-percent { background: rgba(155,89,182,0.1); color: #9b59b6; }
.stat-info {
  display: flex;
  flex-direction: column;
}
.stat-number {
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1;
}
.stat-label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin-top: var(--spacing-xs);
}

/* Charts */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--spacing-base);
  margin-bottom: var(--spacing-xl);
}
.chart-card {
  padding: var(--spacing-lg);
}
.chart-bars {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
.bar-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}
.bar-label {
  width: 110px;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-shrink: 0;
}
.quarto-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.bar-track {
  flex: 1;
  display: flex;
  height: 28px;
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.5s ease;
  min-width: 0;
}
.bar-fill-pending {
  background: #ddd;
}
.bar-value {
  font-size: var(--text-xs);
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}
.bar-fill-pending .bar-value {
  color: var(--color-text-muted);
  text-shadow: none;
}
.bar-total {
  width: 45px;
  text-align: right;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-muted);
  flex-shrink: 0;
}
.chart-legend {
  display: flex;
  gap: var(--spacing-base);
  margin-top: var(--spacing-base);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-bg-separator);
}
.legend-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.legend-confirmed { background: var(--color-success); }
.legend-pending { background: #ddd; }

/* Ranking */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
.ranking-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}
.ranking-position {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-sm);
  font-weight: 700;
  background: var(--color-bg-hover);
  color: var(--color-text-muted);
  flex-shrink: 0;
}
.ranking-1 { background: #f1c40f; color: #fff; }
.ranking-2 { background: #95a5a6; color: #fff; }
.ranking-3 { background: #cd7f32; color: #fff; }
.ranking-name {
  width: 100px;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  flex-shrink: 0;
}
.ranking-bar-track {
  flex: 1;
  height: 24px;
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.ranking-bar-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: var(--radius-md);
  transition: width 0.5s ease;
}
.ranking-count {
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-accent);
  width: 30px;
  text-align: right;
  flex-shrink: 0;
}
.chart-empty {
  text-align: center;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  padding: var(--spacing-lg) 0;
}

/* Recent */
.section-title {
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-base);
}
.recent-list {
  padding: 0;
}
.recent-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-base);
  padding: 14px var(--spacing-lg);
  border-bottom: 1px solid var(--color-bg-separator);
}
.recent-item:last-child {
  border-bottom: none;
}
.recent-name {
  font-weight: 600;
  color: var(--color-text-primary);
  flex: 1;
}
.recent-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.recent-by {
  font-size: var(--text-xs);
  color: var(--color-accent);
  font-weight: 600;
}
.recent-time {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
@media (max-width: 600px) {
  .charts-grid { grid-template-columns: 1fr; }
  .bar-label { width: 80px; font-size: var(--text-xs); }
  .ranking-name { width: 80px; font-size: var(--text-xs); }
}
</style>
